import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { plots } from "@/mocks/plots";

export default function PlotDetail() {
  const { id } = useParams<{ id: string }>();
  const plot = plots.find((item) => item.id === id);

  if (!plot) {
    return (
      <div className="mx-auto max-w-site px-4 py-20 md:px-6">
        <EmptyState
          title="不存在此剧情解析"
          description="你访问的解析文章不存在或链接已失效，请返回剧情解析列表重新选择。"
          secondaryLabel="返回剧情解析"
          secondaryLink="/category/plots"
        />
      </div>
    );
  }

  const related = plots.filter((item) => item.id !== plot.id).slice(0, 3);
  const fullText = plot.sections.map((section) => section.paragraphs.join("")).join("");

  return (
    <>
      <PageMeta
        title={`${plot.title} - 年轻的母亲1官网`}
        description={`${plot.excerpt}`}
        keywords={`年轻的母亲1,${plot.tags.join(",")},${plot.title}`}
      />

      <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "剧情解析", to: "/category/plots" }, { label: plot.title }]} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <article>
            <h1 className="text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{plot.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {plot.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {plot.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {plot.readTime}
              </span>
              {plot.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 w-full aspect-video overflow-hidden rounded-lg bg-background-200">
              <img
                src={plot.thumbnail}
                alt={plot.title}
                title={`年轻的母亲1 剧情解析 ${plot.title}`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <p className="mt-6 rounded-lg border-l-4 border-accent-500 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
              {plot.excerpt}
            </p>

            <div className="mt-8">
              {plot.sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-8 scroll-mt-24">
                  <h2 className="mb-3 text-xl font-bold text-foreground-950">{section.heading}</h2>
                  <div className="space-y-4">
                    {section.paragraphs.map((paragraph, index) => (
                      <p key={index} className="text-[15px] leading-loose text-foreground-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
              <Link
                to="/category/plots"
                className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回剧情解析列表
              </Link>
              <span className="text-xs text-foreground-400">全文约 {fullText.length} 字 · 阅读时长 {plot.readTime}</span>
            </div>
          </article>

          <aside>
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-list-check text-primary-500" />
                  文章目录
                </h3>
                <nav className="mt-3 space-y-1">
                  {plot.sections.map((section, index) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      <span className="flex h-5 w-5 items-center justify-center rounded bg-background-100 text-[11px] text-foreground-500">
                        {index + 1}
                      </span>
                      <span className="line-clamp-1">{section.heading}</span>
                    </a>
                  ))}
                </nav>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-links-line text-accent-500" />
                  相关文章推荐
                </h3>
                <div className="space-y-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/plot/${item.id}`}
                      className="group block rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300"
                    >
                      <span className="line-clamp-2 block text-sm font-medium text-foreground-800 group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">{item.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}